import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, Camera } from 'lucide-react';
import AddMusicModal from './AddMusicModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function MusicButton({ onAccessGranted }) {
  const [showScanner, setShowScanner] = useState(false);
  const [showAddMusic, setShowAddMusic] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const html5QrRef = useRef(null);
  const scannerRunning = useRef(false);

  const safeStop = async () => {
    if (html5QrRef.current && scannerRunning.current) {
      scannerRunning.current = false;
      try {
        await html5QrRef.current.stop();
      } catch (err) {
        console.log('[MUSIC] Scanner already stopped');
      }
    }
  };

  const startScanner = async () => {
    setError('');
    setMessage('');
    setScanning(true);
    
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      await new Promise(r => setTimeout(r, 300));
      
      const scanner = new Html5Qrcode("music-qr-reader");
      html5QrRef.current = scanner;
      scannerRunning.current = true;
      
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          // Stop scanner
          await safeStop();
          setScanning(false);
          
          // Validate QR code
          handleScan(decodedText);
        },
        () => {} // onScanFailure - ignore
      );
    } catch (err) {
      console.error('[MUSIC] Camera error:', err);
      setError('Δεν ήταν δυνατή η πρόσβαση στην κάμερα');
      setScanning(false);
      scannerRunning.current = false;
    }
  };

  const stopScanner = async () => {
    await safeStop();
    setScanning(false);
  };

  const handleScan = async (code) => {
    console.log('[MUSIC] QR Code scanned:', code);
    
    try {
      const response = await axios.post(
        `${API}/music/validate-qr`,
        null,
        {
          params: { code: code },
          withCredentials: true
        }
      );
      
      console.log('[MUSIC] Validation response:', response.data);
      
      setMessage('✅ Πρόσβαση για 1 ώρα!');
      
      setTimeout(() => {
        setShowScanner(false);
        setMessage('');
        setShowAddMusic(true);
        if (onAccessGranted) onAccessGranted();
      }, 1500);
    } catch (error) {
      console.error('[MUSIC] QR validation error:', error);
      console.error('[MUSIC] Error response:', error.response?.data);
      
      // Show detailed error
      const errorMsg = error.response?.data?.detail || 'Σφάλμα επικύρωσης';
      setMessage(`❌ ${errorMsg}`);
      
      // Restart scanner after error
      setTimeout(() => {
        setMessage('');
        startScanner();
      }, 3000);
    }
  };

  const handleMusicClick = async () => {
    // Check if user already has access
    try {
      const response = await axios.get(`${API}/music/check-access`, { withCredentials: true });
      if (response.data.has_access) {
        setShowAddMusic(true);
      } else {
        setShowScanner(true);
      }
    } catch (error) {
      setShowScanner(true);
    }
  };

  const handleClose = async () => {
    await stopScanner();
    setShowScanner(false);
    setMessage('');
    setError('');
  };

  // Auto-start scanner when modal opens
  useEffect(() => {
    if (showScanner) {
      startScanner();
    }
    
    return () => {
      safeStop();
    };
  }, [showScanner]);

  return (
    <>
      {/* Music Button */}
      <button
        onClick={handleMusicClick}
        className="text-2xl hover:scale-110 transition-transform"
        title="Προσθήκη Μουσικής"
      >
        🎵
      </button>

      {/* QR Scanner Modal */}
      {showScanner && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[9999] p-4"
          onClick={handleClose}
        >
          <div 
            className="bg-white rounded-lg p-6 max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                <h2 className="text-xl font-medium">Σκάναρε QR Code</h2>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scanner Container */}
            <div id="music-qr-reader" className="w-full rounded-lg overflow-hidden"></div>

            {/* Error */}
            {error && (
              <div className="mt-4 p-3 bg-red-100 text-red-800 rounded-lg text-center">
                {error}
              </div>
            )}

            {/* Message */}
            {message && (
              <div className={`mt-4 text-center p-3 rounded-lg ${
                message.includes('✅') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {message}
              </div>
            )}

            {/* Instructions */}
            {!message && !error && (
              <>
                <div className="mt-4 text-center text-sm text-gray-600">
                  <p className="font-medium">Στόχευσε την κάμερα στο QR code</p>
                  <p className="text-xs mt-1">Ζήτησε από το προσωπικό να σου δείξει τον κωδικό</p>
                </div>
                
                {/* Manual Input για testing */}
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-xs text-gray-600 mb-2">Ή βάλε τον κωδικό χειροκίνητα:</label>
                  <input
                    type="text"
                    placeholder="DIMAKAS_MUSIC_ACCESS_2024"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    onKeyPress={async (e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        await stopScanner();
                        handleScan(e.target.value);
                      }
                    }}
                  />
                </div>
              </>
            )}

            {/* Stop Button */}
            {scanning && (
              <button
                onClick={stopScanner}
                className="mt-4 w-full py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-800 font-medium transition-colors"
              >
                Σταμάτημα Σάρωσης
              </button>
            )}
          </div>
        </div>
      )}

      {/* Add Music Modal */}
      <AddMusicModal
        isOpen={showAddMusic}
        onClose={() => setShowAddMusic(false)}
        onSuccess={() => {
          // Success handled in modal
        }}
      />
    </>
  );
}

export default MusicButton;
